import { useCallback, useEffect } from 'react';
import { postalCodeLocaleToCountryCode } from '../helpers/locale-helpers';
import { useNonReactiveDynamicFormStore } from '../page-contents/filling-experience/components/dynamic-form-container/store';
import { useConfigShallowStore } from '../stores';
import type { AutosaveCoordinator } from './autosave-coordinator';
import { captureDraftSnapshot } from './draft-serializer';

interface DraftAutosaveBridgeProps {
  coordinator: AutosaveCoordinator;
}

/**
 * Feeds dynamic-form store changes into the autosave coordinator. Must render inside `DynamicFormProvider`.
 * The state at mount (restored draft, prefill, autofill) becomes the baseline, so hydration never saves (FR-07).
 */
export const DraftAutosaveBridge = ({ coordinator }: DraftAutosaveBridgeProps) => {
  const store = useNonReactiveDynamicFormStore();
  const { postalCodeLocale } = useConfigShallowStore('postalCodeLocale');
  const countryCode = postalCodeLocaleToCountryCode(postalCodeLocale);

  const capture = useCallback(() => {
    const { formFieldsMap, activeSectionIndex } = store.getState();
    return captureDraftSnapshot({ formFieldsMap, activeSectionIndex, countryCode });
  }, [store, countryCode]);

  useEffect(() => {
    coordinator.setBaseline(capture());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinator]); // a later country change is an edit, not a new baseline

  useEffect(() => {
    coordinator.notifyChange(capture());

    return store.subscribe((state, prevState) => {
      if (
        state.formFieldsMap === prevState.formFieldsMap &&
        state.activeSectionIndex === prevState.activeSectionIndex
      ) {
        return;
      }
      coordinator.notifyChange(capture());
    });
  }, [store, coordinator, capture]);

  return null;
};
